import isomorphicFetch from 'isomorphic-fetch'; // eslint-disable-line
import io from 'socket.io-client';
import { validateConfig, validateResponse } from './schemas';
import {
    getServerCookieByName,
    getClientCookieByName,
    setClientCookieByName,
    setServerCookieByName,
    parseOldRequestString,
    parseNewRequestString
} from './helpers';
import { EventEmitter } from 'events';
import DebounceManager from './debounceManager';

//TODO: remove this line
//let __CLIENT__ = true;
//let __SERVER__ = false;

const COOKIE_EXPIRES_TIMESTAMP = 2147483647; // Tue Jan 19 2038 06:14:07
const TOKEN_ERROR_CODES = [
    -33404,
    -33400,
    -32001
];
const DEVICE_ID_ERROR_CODES = [
    -32600,
    -33402
];

const INVALID_RESPONSE_ERROR = {
    httpCode: 500,
    message : 'Invalid response data',
    code    : -39101
};
const NETWORK_ERROR = {
    httpCode: 500,
    message : 'Network Error',
    code    : -39102
};
const FORCED_TIMEOUT_ERROR = {
    httpCode: 408,
    message : 'Request timeout',
    code    : -32408,
    forced  : true
};
const CANCELED_REQUEST = {
    httpCode: 499, // nginx http canceled request code
    message : 'Request canceled',
    code    : -39499
};

const deprecationWarning = function() {
    // __DEVELOPMENT__ && console.warn('Deprecation warning:', ...arguments);
};

const makeGetDeviceIdRequest = (service) => service.request('/service', 'v1', 'getDeviceId');
const makeGetTokenRequest = (service, uuid) => service.request('/service', 'v1', 'getToken', { uuid });

const RESTRICTED_PARAMS_FIELDS = ['password', 'token', 'accessToken', 'access_token', 'pw', 'secret'];

export class CustomResponseError extends Error {

    constructor(message, method, params = {}, responseBody) {
        super(message);

        this.method = method;
        this.params = params;

        RESTRICTED_PARAMS_FIELDS.forEach((field) => {
            if(typeof this.params[field] === 'string') {
                this.params[field] = this.params[field].replace(/./ig, '*');
            }
        });

        this.responceBody = responseBody;
    }

    get errorResponseObject() {
        throw new Error('Not implemented.');
    }

}

export class InvalidResponseDataError extends CustomResponseError {

    constructor(message, method, params, responseBody, realError) {
        super(message, method, params, responseBody);
        this.realError = realError;
    }

    get errorResponseObject() {
        return INVALID_RESPONSE_ERROR;
    }

}

export class ResponseError extends CustomResponseError {

    constructor(message, method, params, responseBody, errorObject) {
        super(message, method, params, responseBody);
        this.errorObject = errorObject;
    }

    get errorResponseObject() {
        return this.errorObject;
    }

}

export class NetworkError extends CustomResponseError {

    constructor(message, method, params, responseBody, realError) {
        super(message, method, params, responseBody);
        this.realError = realError;
    }

    get errorResponseObject() {
        return NETWORK_ERROR;
    }

}

if(!(__SERVER__ || __CLIENT__)) {
    throw new Error('Invalid environment: check "define plugin" for __SERVER__ and __CLIENT__ variables');
}

class Client extends EventEmitter {

    constructor(config = {}) {
        super();

        const { timeout } = config;

        this.config = config;
        this.config.timeout = timeout || 20000;
    }

    request(namespace, version, method, params, meta) {
        const { timeout } = this.config;

        return new Promise((resolve, reject) => {
            let timeoutTriggered = false;
            let timeoutId = null;

            const error = (err) => {
                clearTimeout(timeoutId);

                if(err) {
                    reject(err);
                } else {
                    timeoutTriggered = true;

                    reject(FORCED_TIMEOUT_ERROR);
                }
            };

            timeoutId = setTimeout(() => {
                error();
            }, timeout);

            const ok = (data) => {
                clearTimeout(timeoutId);

                if(timeoutTriggered) {
                    return;
                }

                resolve(data);
            };

            this._request(namespace, version, method, params, meta)
                .then(ok)
                .catch(error)
        });
    }

    on() {
        throw new Error('Not implemented');
    }

    once() {
        throw new Error('Not implemented');
    }

    off() {
        throw new Error('Not implemented');
    }

    _request() {
        throw new Error('Not implemented');
    }

}

class RestFetcher extends Client {

    constructor() {
        super(...arguments);
        this.type = 'rest';
    }

    on() {
        // noop
    }

    once() {
        // noop
    }

    off() {
        // noop
    }

    _request(namespace, version, method, params, meta = {}) {
        let agent = null;
        const { applicationName, pathPrefix, host } = this.config;

        if(__SERVER__) {
            if(/^https/.test(host)) {
                const https = require('https');

                agent = new https.Agent({
                    rejectUnauthorized: false
                })
            }
        }
        const url = `${host}${pathPrefix}${namespace}/${version}/${method}`;
        const paramsString = JSON.stringify(params);

        return fetch(url, {
            method     : 'POST',
            body       : paramsString,
            credentials: 'include',
            headers    : {
                Accept              : 'application/json',
                'x-api-cred'        : `${meta.uuid}|${meta.token}`,
                'content-type'      : 'application/json',
                'x-application-name': applicationName
            },
            agent
        })
            .then((res) => res.json()) // да да, именно text, я хочу сохранить исходный ответ для логгирования.
            .then((json) => {
                if(json.httpCode === 200) {
                    if(json && json.result !== undefined) {
                        return json;
                    } else {
                        throw new InvalidResponseDataError('Invalid response data', url, params, json, null);
                    }
                } else if(json.error) {
                    // пробросить в catch объект ошибки;
                    throw new ResponseError('Response error', url, params, json, json.error);
                } else {
                    throw new InvalidResponseDataError('Unknown response error', url, params);
                }
            })
            .catch((error) => {
                switch(true) {
                    case error instanceof SyntaxError: {
                        throw new InvalidResponseDataError('JSON parse error', url, params, json, error);
                    }
                    case error instanceof TypeError: { // fetch API выбрасывает TypeError на ошибки сети и cors
                        throw new NetworkError(error.message, url, params, null, error);
                    }
                    case error instanceof InvalidResponseDataError: {
                        throw error;
                    }
                    default: {
                        // ошибка может быть внезапно не в ожидаемом формате.
                        throw error;
                    }
                }
            });
    }

}

class SocketIOFetcher extends Client {

    constructor() {
        super(...arguments);

        this.type = 'socketio';
        this._masterSocket = null;
        this._ready = false;
        this._rId = 0;
        this._IOClients = {};
        this._noWait = this.config.noWait;
        this._subscriptions = [];
        this._requests = [];
    }

    on(namespace, version, event, handler) { // eslint-disable-line no-unused-vars
        this._subUnsub('on', ...arguments);

        return this;
    }

    off(namespace, version, event, handler) { // eslint-disable-line no-unused-vars
        this._subUnsub('off', ...arguments);

        return this;
    }

    once(namespace, version, event, handler) { // eslint-disable-line no-unused-vars
        this._subUnsub('once', ...arguments);

        return this;
    }

    _subUnsub(kind, namespace, version, event, handler) {
        const args = arguments;
        const makeSubscription = () => {
            if(args.length === 3) {
                event = namespace;
                handler = version;
                this._masterSocket[kind](event, handler);
            } else {
                this._getIOClient(namespace, version).on(event, handler);
            }
        };

        if(this._noWait) {
            this._subscriptions.push(makeSubscription);
        } else {
            makeSubscription();
        }
    }

    _pushCall(request) {
        if(this._requests.length > this.config.queueLength) {
            const { reject } = this._requests.shift();

            reject(CANCELED_REQUEST);
        }
        this._requests.push(request);
    }

    _request() {
        const performRequest = (namespace, version, method, params, meta) => new Promise((resolve, reject) => {
            const client = this._getIOClient(namespace, version);
            const id = ++this._rId;

            client.once(`message:${id}`, (data) => {
                const fullMethodName = `${this.config.host}${namespace}/${version}/${method}`;

                if(!validateResponse(data).valid) {
                    reject(new InvalidResponseDataError('Invalid response data', fullMethodName, params, data, null));

                    return;
                }

                if(data.httpCode !== 200) {
                    reject(new ResponseError('Response error', fullMethodName, params, data, data.error));

                    return;
                }

                resolve(data);
            });

            client.emit('message', {
                method,
                params,
                id,
                meta
            });
        });

        if(this._noWait && !this._ready) {
            __DEVELOPMENT__ && console.warn('Adding request to queue', arguments)

            return new Promise((resolve, reject) => {
                this._pushCall({
                    resolve,
                    reject,
                    request: performRequest.bind(null, ...arguments)
                });
            });
        }

        return performRequest(...arguments);
    }

    _getIOClient(namespace, version) {
        const fullNamespace = `${namespace}/${version}`;

        if(this._IOClients[fullNamespace]) {
            return this._IOClients[fullNamespace];
        } else {
            const client = io(`${this.config.host}${fullNamespace}`);

            this._IOClients[fullNamespace] = client;

            return client;
        }
    }

    init() {
        return new Promise((resolve, reject) => {
            const { noWait, host, usePolling, useWebsocket } = this.config;

            let protocols = [],
                onConnected,
                onError;

            // важен порядок транспортов!
            // сначала поллинг, потом сокет
            if(usePolling) {
                protocols.push('polling');
            }

            if(useWebsocket) {
                protocols.push('websocket');
            }

            const socket = io(host, {
                transports: protocols
            });

            if(noWait) {
                resolve(this);
            }

            onError = (error) => {
                onConnected && socket.off('connect', onConnected);

                reject(error);
            };

            onConnected = async () => {
                onError && socket.off('error', onError);

                this._ready = true;
                [
                    'connect',
                    'reconnect',
                    'disconnect',
                    'error',
                    'reconnecting'
                ].forEach((eventName) => {
                    socket.on(eventName, (data) => {
                        this.emit(eventName, data);
                    })
                });

                this.emit('connect');

                if(noWait) {
                    this._subscriptions.forEach((func) => func());

                    for(let i = 0; i < this._requests.length; ++i) {
                        const { request, resolve, reject } = this._requests[i];

                        try {
                            __DEVELOPMENT__ && console.warn('Executing queued request');

                            const res = await request(); // eslint-disable-line no-await-in-loop

                            resolve(res);
                        } catch(err) {
                            reject(err);
                        }
                    }
                } else {
                    resolve(this);
                }
            };

            socket.once('connect', onConnected);
            socket.once('error', onError);

            this._masterSocket = socket;
        });
    }

}

class Service {

    constructor({ applicationName, serviceName, host, duplexMethods, timeout, protocol, queued, socketIOQueueLength, noWait }) {
        const { websocket, polling, rest, restURLPathPrefix } = protocol;

        this.applicationName = applicationName;
        this.serviceName = serviceName;
        this._websocket = __SERVER__ ? false : websocket; // на сервере никогда не будет ни вебсокета ни поллинга (это не имеет смысла)
        this._polling = __SERVER__ ? false : polling;
        this._rest = __SERVER__ ? true : rest;      // на сервере всегда работает REST
        this._queued = queued;
        this._noWait = noWait;

        let prefix = restURLPathPrefix;

        if(!restURLPathPrefix.startsWith('/')) {
            prefix = `/${restURLPathPrefix}`;
        }

        this._restURLPathPrefix = prefix;

        if(__SERVER__) {
            this._timeout = timeout.server || (20 * 1000);
            this.host = host.server;
        } else if(__CLIENT__) {
            this._timeout = timeout.client || (20 * 1000);
            this.host = host.client;
        }

        this._socketIOQueueLength = socketIOQueueLength || 10;

        if(!this.host.startsWith('http')) {
            throw new Error(`Invalid transport host ${this.host} for ${serviceName}. URI scheme not specified.`);
        }

        if(!/^https?:\/\/[A-Za-z0-9.-]+(:\d+)?$/.test(this.host)) {
            throw new Error(`Invalid transport host. Trailing "/" and URI path not allowed. Service: [${serviceName}], host [${this.host}].`);
        }

        this._duplexMethods = duplexMethods || [];
        this._restClient = null;
        this._socketIOClient = null;
        this._debounceManager = new DebounceManager();
    }

    init() {
        if(this._websocket || this._polling) {
            this._socketIOClient = new SocketIOFetcher({
                host           : this.host,
                applicationName: this.applicationName,
                useWebsocket   : this._websocket,
                usePolling     : this._polling,
                timeout        : this._timeout,
                queued         : this._queued,
                queueLength    : this._socketIOQueueLength,
                noWait         : this._noWait
            });
        }

        if(this._rest) {
            this._restClient = new RestFetcher({
                host           : this.host,
                applicationName: this.applicationName,
                pathPrefix     : this._restURLPathPrefix,
                timeout        : this._timeout
            });
        }

        if(!this._socketIOClient && !this._restClient) {
            //TODO error message
            throw new Error('Something goes wrong!');
        }

        if(this._socketIOClient) {
            return this._socketIOClient.init();
        }

        return Promise.resolve(this);
    }

    on() {
        if(this._socketIOClient) {
            this._socketIOClient.on(...arguments);
        }
    }

    once() {
        if(this._socketIOClient) {
            this._socketIOClient.once(...arguments);
        }
    }

    off() {
        if(this._socketIOClient) {
            this._socketIOClient.off(...arguments);
        }
    }

    request(namespace, version, method, params, meta, debounce) {
        const isMethodDuplex = this._duplexMethods.indexOf(`${namespace}/${version}/${method}`) !== -1;
        let client;
        // если метод требует двунаправленного взаимодействия ИЛИ если rest отключен на клиенте принудительно

        if((isMethodDuplex && this._socketIOClient) || (!isMethodDuplex && !this._restClient && this._socketIOClient)) {
            client = this._socketIOClient;
        } else {
            client = this._restClient;
        }

        return new Promise((resolve, reject) => {
            if(debounce) {
                this._debounceManager.add(debounce, params, reject);
            }

            client.request(namespace, version, method, params, meta)
                .then(resolve)
                .catch(reject);
        });
    }

}

class CookieProvider {

    constructor(req, res) {
        this.req = req || null;
        this.res = res || null;
        this.cookies = {}; // server workaround;
    }

    get(name) {
        if(this.req && this.res && __SERVER__) {
            const reqCookie = getServerCookieByName(this.req, name);

            if(!this.cookies[name]) {
                this.cookies[name] = reqCookie;

                return reqCookie;
            } else {
                return this.cookies[name];
            }
        } else if(__CLIENT__) {
            return getClientCookieByName(name);
        } else {
            throw new Error('Cookie get error: invalid environment');
        }
    }

    set(name, value) {
        this.cookies[name] = value;

        if(this.req && this.res && __SERVER__) {
            setServerCookieByName(this.res, name, value, COOKIE_EXPIRES_TIMESTAMP);
        } else if(__CLIENT__) {
            setClientCookieByName(name, value, COOKIE_EXPIRES_TIMESTAMP);
        } else {
            throw new Error('Cookie set error: invalid environment');
        }
    }

}

/**
 * Событие, предназначенное для логгирования ошибок обращение к внешним сервисам.
 *
 * @event module:Transport~error
 * @property {object} Объект ошибки
 */

/**
 * Событие,
 * @event module:Transport~message
 * @property eventObject {object} Объект сообщения
 * @property eventObject.test {object} Сообщение
 */

/**
 * @module Transport
 * @type {Transport}
 */
class Transport extends EventEmitter {

    constructor(cookieProvider, services) {
        super();

        if(!cookieProvider || cookieProvider.constructor !== CookieProvider) {
            throw new Error('Invalid cookie provider instance');
        }
        this.cookieProvider = cookieProvider;
        this.services = services;

        this._deviceId = null;
        this._token = null;

        this._queue = [];
        this._queuing = false;
    }

    _subUnsubEventEmitter(kind, serviceName, fullNamespace, event, handler) {
        const args = Array.from(arguments).filter((value) => !!value);
        const kindMap = {
            on  : 'addListener',
            off : 'removeListener',
            once: 'prependOnceListener'
        };

        if(args.length === 3) {
            event = serviceName;
            handler = fullNamespace;

            if(event === 'requestError') {
                this[kindMap[kind]](event, handler);

                return this;
            }

            if(~['connect', 'disconnect', 'reconnect', 'reconnecting'].indexOf(event)) {
                Reflect.ownKeys(this.services).forEach((serviceName) => this.services[serviceName][kind](event, handler));

                return this;
            }
        }

        if(__SERVER__) {
            throw new Error('Transport event subscription not available on server side!');
        }

        if(args.length <= 4) {
            // old request string style style
            try {
                const [_, connectionString, event, handler] = args; // eslint-disable-line no-unused-vars
                const { serviceName, namespace, version } = parseOldRequestString(connectionString);
                const service = this.services[serviceName];

                if(!service) {
                    throw new Error('Invalid service name');
                }

                service[kind](namespace, version, event, handler);

                deprecationWarning(`transport.${kind} invalid arguments length. Use transport.${kind}(serviceName, "/namespace/version/event", handler)`);

                return this;
            } catch(e) { /* noop */
                console.error(e);
            }

            const [_, serviceName, connectionString, handler] = args; // eslint-disable-line no-unused-vars
            const { namespace, version, method/*event*/ } = parseNewRequestString(connectionString);
            const service = this.services[serviceName];

            if(!service) {
                throw new Error('Invalid service name');
            }

            service[kind](namespace, version, method, handler);

            return this;
        }

        if(args.length === 5) { // new request string style
            const service = this.services[serviceName];

            if(!service) {
                throw new Error('Invalid service name');
            }

            service[kind](fullNamespace, event, handler);

            return this;
        }

        throw new Error('Invalid arguments length');
    }

    on(serviceName, fullNamespace, event, handler) { // eslint-disable-line no-unused-vars
        return this._subUnsubEventEmitter('on', ...arguments);
    }

    once(serviceName, fullNamespace, event, handler) { // eslint-disable-line no-unused-vars
        return this._subUnsubEventEmitter('once', ...arguments);
    }

    off(serviceName, fullNamespace, event, handler) { // eslint-disable-line no-unused-vars
        return this._subUnsubEventEmitter('off', ...arguments);
    }

    _setCookie(name, value) {
        this.cookieProvider.set(name, value);
    }

    _getCookie(name) {
        return this.cookieProvider.get(name);
    }

    _getMeta() {
        return {
            uuid : this._getCookie('deviceId'),
            token: this._getCookie('token')
        };
    }

    _setDeviceId(id = null) {
        this._deviceId = id;
        this._setCookie('deviceId', id);
    }

    _setToken(token = null) {
        this._token = token;
        this._setCookie('token', token);
    }

    /**
     * @requestString
     * @param {String} serviceName имя сервиса, задается в конфиге транспорта (например booker)
     * @param {String} requestString строка, содержащее имя эндпоинта, версию и метод, например "/events/v1/eventsList"
     * @param {Object} params параметр запроса в виде:
     * @param {Object} [options={}] options опции запроса
     * @param {Boolean|Null|Number} [options.debounce=false] options.debounce таймер "дебаунса" запроса, задается в ms
     *   - если параметр передан, то если за указанную константу времени было вызвано несколько запросов с одинаковыми параметрами
     *   - каждый предыдущий запрос, который не успел выполнится будет отменен с объектом ошибки со следующими параметрами:
     *   - { httpCode: 408, message : 'Debounce timeout', code : -32409 }
     * @returns {Promise<any>} Promise
     */
    request(serviceName, requestString, params = {}, { debounce = null } = {}) {
        const { namespace, version, method } = parseNewRequestString(requestString);

        return this._request({
            serviceName,
            namespace,
            version,
            method,
            params,
            debounce
        });
    }

    /**
     * @method
     * @deprecated use "request" method
     * @param {String} requestString строка, содержащее имя подключения и имя эндпоинта в формате "apiName./endpointName/version-v1"
     * @param {Object} data объект запроса
     * @param {String} data.method метод
     * @param {Number|String|Array<Number|String|Object>|Object} data.params.* произвольные параметры запроса
     * @param {Object} [options={}] options опции запроса
     * @param {Boolean|Null|Number} [options.debounce=false] options.debounce таймер "дебаунса" запроса, задается в ms
     *   - если параметр передан, то если за указанную константу времени было вызвано несколько запросов с одинаковыми параметрами
     *   - каждый предыдущий запрос, который не успел выполнится будет отменен с объектом ошибки со следующими параметрами:
     *   - { httpCode: 408, message : 'Debounce timeout', code : -32409 }
     * @fires module:Transport~error
     * @returns {Promise<any>} Promise
     */
    publish(requestString, data = {}, { debounce = false } = {}) {
        deprecationWarning('Use transport.request(...) instead of transport.publish(...)');

        const { serviceName, namespace, version } = parseOldRequestString(requestString) || [];
        const { method, params } = data;

        return this._request({
            serviceName,
            namespace,
            version,
            method,
            params,
            debounce
        });
    }

    async _dequeue() {
        if(!this._queue.length || this._queuing) {
            return;
        }

        this._queuing = true;

        let failsafe = 0;
        let item = null;

        while((item = this._queue.pop())) {
            const { request, resolve, reject, service } = item;

            failsafe++;

            if(failsafe > 20) { // просто на всякий случай
                console.warn('Potential infinite loop');
                reject(INVALID_RESPONSE_ERROR);
                continue;
            }

            try {
                const response = await request(); // eslint-disable-line no-await-in-loop

                if(response) {
                    if(response.meta && response.meta.token) {
                        this._setToken(response.meta.token);
                    }

                    resolve(response.result);
                    continue;
                }
            } catch(err) {
                try {
                    const { code } = err;

                    if(~DEVICE_ID_ERROR_CODES.indexOf(code)) {
                        const { result } = await makeGetDeviceIdRequest(service); // eslint-disable-line no-await-in-loop

                        this._setDeviceId(result);
                        this._enqueue(item, true);
                        continue;
                    }

                    if(~TOKEN_ERROR_CODES.indexOf(code)) {
                        const { result } = await makeGetTokenRequest(service, this._getMeta().uuid); // eslint-disable-line no-await-in-loop

                        this._setToken(result.token);
                        this._enqueue(item, true);
                        continue;
                    }
                    reject(err);
                } catch(err) {
                    reject(err);
                }
            }
        }
        this._queuing = false;
        this._dequeue();
    }

    _enqueue({ request, resolve, reject, service }, append) {
        if(append) {
            this._queue.push({
                request,
                resolve,
                reject,
                service
            });
        } else {
            this._queue.unshift({
                request,
                resolve,
                reject,
                service
            });
        }
        this._dequeue();

        return this;
    }

    _request({ serviceName, namespace, version, method, params = {}, debounce } = {}) {
        const service = this.services[serviceName];

        if(!service || !(service instanceof Service)) {
            throw new Error('Invalid service name');
        }

        return new Promise(async (resolve, reject) => {
            const request = () => {
                return service.request(namespace, version, method, params, this._getMeta(), debounce)
                    .catch((err) => {
                        this.emit('requestError', err);
                        if(err instanceof CustomResponseError) {
                            throw err.errorResponseObject;
                        }

                        reject(err);
                    });
            };

            try {
                const response = await request();

                if(!response) {
                    throw new InvalidResponseDataError('Empty response', method, params, null, null);
                }

                if(response.meta && response.meta.token) {
                    this._setToken(response.meta.token);
                }

                resolve(response.result);
            } catch(err) {
                if(~TOKEN_ERROR_CODES.indexOf(err.code) || ~DEVICE_ID_ERROR_CODES.indexOf(err.code)) {
                    this._enqueue({
                        request,
                        resolve,
                        reject,
                        service //TODO костылька
                    });
                } else {
                    reject(err);
                }
            }
        });
    }

}

class TransportFactory {

    constructor(config) {
        const configValidationResult = validateConfig(config);

        if(!configValidationResult.valid) {
            throw new Error(`Invalid transport config: \n${configValidationResult.toString()}`);
        }

        this.config = config;
        this.services = {};

        Object.freeze(this.config);
    }

    getServerInstance(req, res) {
        if(arguments.length < 2) {
            throw new Error('Invalid arguments length');
        }

        if(__CLIENT__) {
            throw new Error('Method "getServerInstance" called in client environment, use "getClientInstance()"');
        }

        return new Transport(new CookieProvider(req, res), this.services);
    }

    getClientInstance() {
        if(__SERVER__) {
            throw new Error('Method "getClientInstance" called in server environment, use "getServerInstance(req, res)"');
        }

        return new Transport(new CookieProvider(), this.services);
    }

    async init() {
        const servicesPromises = [];

        for(let serviceConfigName of Object.getOwnPropertyNames(this.config)) {
            const serviceConfig = this.config[serviceConfigName];
            const { serviceName } = serviceConfig;

            if(this.services[serviceName]) {
                throw new Error(`Duplicate service name: ${serviceName}`);
            }

            const service = new Service(serviceConfig);

            servicesPromises.push(service.init());
            this.services[serviceConfig.serviceName] = service;
        }

        Object.freeze(this.services);

        await Promise.all(servicesPromises);

        return this;
    }

}

export default TransportFactory;



// WEBPACK FOOTER //
// ./node_modules/core-ui__transport/index.js